import { Alert, Col } from 'antd';
import { FormItem } from 'modules/common/components';
import { getValueByPath } from 'modules/common/utils/getValueByPath';
import React, { FC } from 'react';
import { useFormContext } from 'react-hook-form';
import { useTranslation } from 'react-i18next';

import styles from './styles.module.scss';

import { PetField } from '../fields';
import { SectionFormField } from '../types';

type PropTypes = {
  fields: SectionFormField[];
  fieldNamePrefix: string;
  isDaycareLocationSelected?: boolean;
  showMicrochipAlert?: boolean;
  microchipAlertMessage?: string;
  showAgeWarning?: boolean;
  ageWarningMessage?: string;
};

const PetFormSection: FC<PropTypes> = ({
  fields,
  fieldNamePrefix,
  isDaycareLocationSelected,
  showMicrochipAlert,
  microchipAlertMessage,
  showAgeWarning,
  ageWarningMessage,
}) => {
  const { t } = useTranslation('donor.RegisterPetForm');

  const { control, formState, watch } = useFormContext();
  const doesPetTakeMedications = watch(
    `${fieldNamePrefix}.${PetField.TakeMedications}`
  );
  return (
    <>
      {fields.map(({ Component, name, label, tooltip }) => {
        const fullFieldName = `${fieldNamePrefix}.${name}`;

        const error = getValueByPath(formState.errors, fullFieldName);

        const isAdditionalDaycareField =
          name === PetField.WontRegularlyAttendDaycare ||
          name === PetField.AttendDaycareDays;
        const isMicrochipField = name === 'hasMicrochip';
        const isDateOfBirthField = name === PetField.DateOfBirth;

        if (isAdditionalDaycareField && !isDaycareLocationSelected) {
          return null;
        }

        if (
          name === PetField.MedicationsCourseComment &&
          !doesPetTakeMedications
        ) {
          return null;
        }
        const isDisabled =
          watch(`${fieldNamePrefix}.${PetField.WontRegularlyAttendDaycare}`) &&
          name === PetField.AttendDaycareDays;

        const extraText = error?.message
          ? error.message
          : isDateOfBirthField && showAgeWarning
          ? ageWarningMessage
          : undefined;

        const validateStatus =
          (error && 'error') ||
          (isDateOfBirthField && showAgeWarning && 'warning') ||
          undefined;

        return (
          <React.Fragment key={fullFieldName}>
            <Col span={24}>
              <FormItem
                label={label}
                extra={extraText}
                validateStatus={validateStatus}
                tooltip={tooltip}
              >
                <Component
                  control={control}
                  name={fullFieldName}
                  disabled={isDisabled}
                />
              </FormItem>
            </Col>

            {isMicrochipField && showMicrochipAlert && (
              <Col span={24} className={styles.microchipAlertContainer}>
                <Alert
                  message={t('microchipRequired')}
                  description={microchipAlertMessage}
                  type="info"
                  showIcon
                  closable={false}
                />
              </Col>
            )}
          </React.Fragment>
        );
      })}
    </>
  );
};

export default PetFormSection;
